import {
  ApolloClient,
  ApolloProvider,
  concat,
  createHttpLink,
  InMemoryCache,
} from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { GuidedTourProvider } from "_shared/contexts/GuidedTourContext";
import { OpenTicketProvider } from "_shared/contexts/OpenTicketContext";
import { Amplify } from "aws-amplify";
import React, { useEffect, useState } from "react";
import { ThemeProvider } from "styled-components";
import { GlobalStyles } from "./_root/styles/GlobalStyles";
import { darkTheme, lightTheme } from "./_root/styles/Themes";
import Loading from "./_shared/components/Loading/Loading";
import { AuthProvider, useAuth } from "./_shared/contexts/AuthContext";
import { ModalProvider } from "./_shared/contexts/ModalContext";
import { StateProvider } from "./_shared/contexts/Store";
import "./App.scss";
import { awsConfig } from "./env";
import Header from "./header/Header";
import { Routes } from "./Routes";
import { PostHogProvider } from 'posthog-js/react'

Amplify.configure(awsConfig);

// configure apollo client
const httpLink = createHttpLink({
  uri: awsConfig.API.graphql_endpoint,
});
const authLink = setContext((_, { headers }) => {
  const token = Object.entries(localStorage)?.find(([key]) =>
    key.endsWith("idToken"),
  )?.[1];
  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : "",
    },
  };
});
const apolloClient = new ApolloClient({
  link: concat(authLink, httpLink),
  cache: new InMemoryCache(),
});

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { analytics } from "_shared/utils/analytics";
import { useLocation } from "react-router-dom";
const queryClient = new QueryClient();

function App() {
  const { pathname } = useLocation();
  const locTheme = window.localStorage.getItem("theme");
  const newTheme = locTheme === null ? "dark" : locTheme;
  const [theme] = useState(newTheme);

  useEffect(() => {
    analytics.track("Page Viewed", {
      previous: history.state?.prev,
    });
  }, [pathname]);

  return (
    <ApolloProvider client={apolloClient}>
      <ModalProvider>
        <AuthProvider>
          <StateProvider>
            <ThemeProvider theme={theme === "light" ? lightTheme : darkTheme}>
              <GuidedTourProvider>
                <OpenTicketProvider>
                  <Layout />
                </OpenTicketProvider>
              </GuidedTourProvider>
            </ThemeProvider>
          </StateProvider>
        </AuthProvider>
      </ModalProvider>
    </ApolloProvider>
  );
}

const Layout: React.FC = () => {
  const { isAuthenticated, isAuthenticating } = useAuth();
  const [isMobile, setIsMobile] = useState(
    window.matchMedia("(max-width: 820px)").matches,
  );

  useEffect(() => {
    const view = localStorage.getItem("view");
    if (view === null) {
      localStorage.setItem("view", "basic");
    }
    const checkMobile = () => {
      setIsMobile(window.matchMedia("(max-width: 820px)").matches);
    };
    window.addEventListener("resize", checkMobile);

    return () => {
      window.removeEventListener("resize", checkMobile);
    };
  }, []);

  return (
    <>
      <PostHogProvider apiKey={import.meta.env.VITE_POSTHOG_KEY} options={{ api_host: import.meta.env.VITE_POSTHOG_HOST }}>
        <QueryClientProvider client={queryClient}>
          {/* Styled component */}
          <GlobalStyles />
          {/* Navigation bar with links */}
          <Header isAuthenticated={isAuthenticated} isMobile={isMobile} />
          {/* App routes */}
          {isAuthenticating ? <Loading message="Signing in..." /> : <Routes />}
        </QueryClientProvider>
      </PostHogProvider>
    </>
  );
};

export default App;
